import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Calendar from '../components/scheduler/Calendar';
import DayOffRequestModal from '../components/scheduler/DayOffRequestModal';
import WorkCertificate from '../components/attendance/WorkCertificate';
import { useSchedule } from '../contexts/ScheduleContext';
import { format, startOfWeek, endOfWeek, addWeeks, subWeeks, isSameWeek, differenceInWeeks, startOfMonth } from 'date-fns';
import { ko } from 'date-fns/locale';

const SchedulerPage: React.FC = () => {
  const { user } = useAuth();
  // Context 사용
  const { scheduleData, addDayOffRequest, dayOffRequests } = useSchedule();
  
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // 주간 페이지네이션 상태
  const [currentWeekStart, setCurrentWeekStart] = useState<Date>(
    startOfWeek(new Date(), { weekStartsOn: 0 }) // 일요일 시작
  );

  const handleDateClick = (date: Date) => {
    // 비활성 계정은 신청 불가
    if (user && !user.isActive) {
      return;
    }
    setSelectedDate(date);
    setIsModalOpen(true);
  };

  const handleRequestSubmit = async (date: Date, reason: string) => {
    try {
      // Context의 addDayOffRequest 호출 (백엔드 API)
      await addDayOffRequest(date, reason);
      
      console.log('휴무 신청 성공:', {
        date: format(date, 'yyyy-MM-dd'),
        reason,
      });
    } catch (error) {
      // 에러는 Context에서 toast로 표시
      console.error('휴무 신청 실패:', error);
    }
  };

  // 이번 주 필터링된 신청 목록
  const filteredRequests = useMemo(() => {
    const weekEnd = endOfWeek(currentWeekStart, { weekStartsOn: 0 });
    
    return dayOffRequests.filter((request) => {
      return request.date >= currentWeekStart && request.date <= weekEnd;
    });
  }, [dayOffRequests, currentWeekStart]);

  // 주간 네비게이션
  const handlePrevWeek = () => {
    setCurrentWeekStart(prev => subWeeks(prev, 1));
  };

  const handleNextWeek = () => {
    const nextWeek = addWeeks(currentWeekStart, 1);
    const thisWeek = startOfWeek(new Date(), { weekStartsOn: 0 });
    
    // 이번 주보다 미래로 못 감
    if (nextWeek <= thisWeek) {
      setCurrentWeekStart(nextWeek);
    }
  };

  const handleThisWeek = () => {
    setCurrentWeekStart(startOfWeek(new Date(), { weekStartsOn: 0 }));
  };

  const isThisWeek = isSameWeek(currentWeekStart, new Date(), { weekStartsOn: 0 });

  // 주차 계산
  const monthStart = startOfMonth(currentWeekStart);
  const weekOfMonth = differenceInWeeks(currentWeekStart, monthStart) + 1;

  return (
    <main className="container mx-auto px-4 py-6 pb-0">
      <div className="max-w-6xl mx-auto">
        {/* 헤더 - 압축 */}
        <div className="text-center mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-primary mb-2">
            📅 AI 스마트 스케줄러
          </h1>
          <p className="text-sm text-gray-600 px-4">
            공정한 휴무 조율과 똑똑한 일정 관리!
          </p>
        </div>

        {/* 주요 기능 카드 - 압축 */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          <div className="card-azocsii text-center p-4 bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-purple-200">
            <div className="text-3xl mb-2">🤖</div>
            <h3 className="text-sm font-bold text-primary mb-1 leading-tight">
              AI<br />수요예측
            </h3>
            <p className="text-xs text-gray-600">
              과거 데이터 기반 필요 인원 자동 예측!
            </p>
          </div>

          <Link to="/lotto" className="card-azocsii text-center p-4 bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-purple-200 hover:border-purple-400 hover:shadow-2xl transition-all duration-200 active:scale-95">
            <div className="text-3xl mb-2">🎲</div>
            <h3 className="text-sm font-bold text-primary mb-1 leading-tight">공정한<br />추첨</h3>
            <p className="text-xs text-gray-600">
              인기 휴무일은 실시간 로또로 공정하게!
            </p>
          </Link>

          <Link to="/swap" className="card-azocsii text-center p-4 bg-gradient-to-br from-blue-50 to-cyan-50 border-2 border-blue-200 hover:border-blue-400 hover:shadow-2xl transition-all duration-200 active:scale-95">
            <div className="text-3xl mb-2">🤝</div>
            <h3 className="text-sm font-bold text-primary mb-1 leading-tight">
              스왑<br />마켓
            </h3>
            <p className="text-xs text-gray-600">
              근무일 교환을 안전하게, 만근수당 걱정 없이!
            </p>
          </Link>
        </div>

        {/* 안내 메시지 - 압축 */}
        <div id="calendar-section" className="bg-base-cream rounded-lg p-4 mb-4 text-center">
          <p className="text-sm font-semibold text-primary mb-1">
            💡 날짜 클릭으로 휴무 신청!
          </p>
          <p className="text-xs text-gray-600">
            캘린더에서 원하는 날짜를 선택하세요
          </p>
        </div>

        {/* 캘린더 */}
        <Calendar 
          onDateClick={handleDateClick} 
          scheduleData={scheduleData} 
          disabled={user ? !user.isActive : false}
        />

        {/* 신청 현황 (주간 페이지네이션) */}
        {dayOffRequests.length > 0 && (
          <div className="mt-6 bg-white rounded-lg shadow-lg overflow-hidden">
            {/* 주간 네비게이션 */}
            <div className="bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 p-4 text-white">
              <div className="text-center mb-3">
                <h3 className="text-lg font-bold">
                  {format(currentWeekStart, 'yyyy년 M월', { locale: ko })} {weekOfMonth}주차
                </h3>
                <p className="text-sm opacity-90">
                  {format(currentWeekStart, 'M/d', { locale: ko })} ~ {format(endOfWeek(currentWeekStart, { weekStartsOn: 0 }), 'M/d', { locale: ko })}
                </p>
              </div>
              
              <div className="flex items-center justify-between gap-2">
                <button
                  onClick={handlePrevWeek}
                  className="px-2 py-1 text-sm bg-white/20 hover:bg-white/30 rounded-lg transition-all font-medium"
                >
                  ◀ 이전 주
                </button>
                
                <div className="text-center">
                  {!isThisWeek && (
                    <button
                      onClick={handleThisWeek}
                      className="px-3 py-1 text-xs bg-white/30 hover:bg-white/40 rounded-full transition-all"
                    >
                      이번 주로 돌아가기
                    </button>
                  )}
                  <p className="text-xs mt-1">
                    이번 주: {filteredRequests.length}건
                  </p>
                </div>
                
                <button
                  onClick={handleNextWeek}
                  disabled={isThisWeek}
                  className={`px-2 py-1 text-sm rounded-lg transition-all font-medium ${
                    isThisWeek 
                      ? 'bg-white/10 text-white/50 cursor-not-allowed' 
                      : 'bg-white/20 hover:bg-white/30'
                  }`}
                >
                  다음 주 ▶
                </button>
              </div>
            </div>

            {/* 신청 목록 */}
            <div className="p-6">
              <h3 className="text-lg font-bold text-primary mb-4">
                📝 신청 현황
              </h3>
              
              {filteredRequests.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <p className="text-4xl mb-2">📭</p>
                  <p className="text-sm">이번 주 신청 내역이 없습니다</p>
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {(() => {
                    // 날짜별로 그룹화 (상태 포함)
                    const grouped = filteredRequests.reduce((acc, request) => {
                      const dateKey = format(request.date, 'yyyy-MM-dd');
                      if (!acc[dateKey]) {
                        acc[dateKey] = { 
                          date: request.date, 
                          count: 0,
                          status: request.status // 첫 번째 요청의 상태 저장
                        };
                      }
                      acc[dateKey].count++;
                      return acc;
                    }, {} as Record<string, { date: Date; count: number; status: string }>);

                    // 날짜 순으로 정렬
                    return Object.values(grouped)
                      .sort((a, b) => a.date.getTime() - b.date.getTime())
                      .map((item) => {
                        // 상태별 색상
                        let bgColor = 'bg-secondary'; // pending (호박색)
                        if (item.status === 'approved') {
                          bgColor = 'bg-green-500'; // 승인 (초록색)
                        } else if (item.status === 'rejected') {
                          bgColor = 'bg-red-400'; // 거절 (빨강)
                        }
                        
                        return (
                          <span
                            key={format(item.date, 'yyyy-MM-dd')}
                            className={`${bgColor} text-white px-3 py-2 rounded-full text-xs font-medium`}
                          >
                            {format(item.date, 'M월 d일')}
                            {item.count > 1 && ` (${item.count})`}
                          </span>
                        );
                      });
                  })()}
                </div>
              )}
            </div>
          </div>
        )}

        {/* 빠른 액션 버튼 */}
        <div className="mt-8 grid md:grid-cols-2 gap-4">
          <button 
            className="btn-azocsii w-full"
            onClick={() => {
              if (user && !user.isActive) return; // 비활성 계정 차단
              setSelectedDate(new Date());
              setIsModalOpen(true);
            }}
            disabled={user ? !user.isActive : false}
            style={user && !user.isActive ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
          >
            ➕ 오늘 휴무 신청하기
          </button>
          <button className="btn-azocsii-outline w-full" disabled>
            🔄 근무일 교환하기 (준비 중)
          </button>
        </div>

        {/* 근무 확인서 */}
        <div className="mt-6">
          <WorkCertificate />
        </div>
      </div>

      {/* 휴무 신청 모달 */}
      <DayOffRequestModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        selectedDate={selectedDate}
        onSubmit={handleRequestSubmit}
      />
    </main>
  );
};

export default SchedulerPage;
